<template>
  <input
    v-if="!file?.exists"
    ref="inputEl"
    type="file"
    hidden
    @change="chosen"
    @cancel="$emit('upload:cancel')"
  />
  <slot
    :file="file"
    :choose="choose"
    :upload="upload"
    :meta="meta"
    :upfile="() => h(UpFile, { file, delayed, onUpload: upload, onRemove: remove, downloadLink })"
  />
</template>
<script setup>
import { computed, h, onMounted, ref, watch } from 'vue'
import { downloadLink, useApi } from '@provise/composables/api'
import config from '@/config'
import UpFile from '@/components/upload/upfile.vue'
import { watchDebounced } from '@vueuse/core'

let uploadMaxFileSize = Number(config.VITE_UPLOAD_MAXFILESIZE)
if (isNaN(uploadMaxFileSize)) {
  uploadMaxFileSize = 3145728 // 3M
}

const props = defineProps({
  url: String,
  division: String,
  // do not upload immediate after file chosen
  delayed: {
    type: Boolean,
    default: false,
  },
  // open filepicker on mount
  chooseOnMount: Boolean,
  modelValue: {
    type: Array,
    default: () => [],
  },
})

const model = ref(props.modelValue || [])
const emit = defineEmits([
  'update:modelValue',
  'upload:remove', // click @remove button
  'upload:cancel', // user just closed filepicker, no file chosen
  'upload:done', // uploaded
  'upload:chosen', // file chosen (triggers before upload start)
])
defineExpose({
  upload,
})

const inputEl = ref(null)
const meta = ref(null)
const fileBlob = ref(null)
const existsFile = computed(() => model.value.find(f => f.division === props.division) || null)
const idx = computed(() =>
  model.value.findIndex(f => existsFile.value?.secure_filename === f.secure_filename)
)
const file = computed(() => {
  if (fileBlob.value) {
    return fileBlob.value
  }
  const f = existsFile.value
  if (!f) {
    return null
  }
  return { name: f.filename, url: f.secure_filename, exists: true }
})

const url = computed(() => props.url)
const {
  refresh: doUpload,
  model: uploaded,
  error: uploadErr,
} = useApi({
  upload: true,
  url,
})
const { refresh: doRemove } = useApi({
  method: 'delete',
})

watch(
  () => existsFile.value,
  val => {
    if (!val) {
      return
    }
    meta.value = val.metadata ?? {}
  },
  {
    immediate: true,
  }
)
watch(
  () => props.modelValue,
  mv => (model.value = mv)
)

watchDebounced(
  meta,
  m => {
    if (idx.value === -1) {
      return
    }
    const a = model.value
    a[idx.value].metadata = m
    model.value = a
  },
  { deep: true, debounce: 500, maxWait: 1000 }
)

onMounted(() => {
  if (props.chooseOnMount && !existsFile.value) {
    choose()
  }
})

function choose() {
  inputEl.value.click()
}

async function chosen() {
  const _file = inputEl.value.files[0]
  if (_file.size > uploadMaxFileSize) {
    fileBlob.value = null
    inputEl.value.value = null
    emit('upload:cancel')
    throw new Error(`Максимальный размер ${uploadMaxFileSize} байт`)
  }
  fileBlob.value = _file
  emit('upload:chosen')
  if (!props.delayed) {
    await upload()
  }
}

async function upload() {
  const data = new FormData()
  const metadata = JSON.stringify(meta.value)
  const m = model.value

  data.append('file', file.value)
  data.append('metadata', metadata)
  data.append('division', props.division)

  try {
    await doUpload({ data })
  } finally {
    fileBlob.value = null
    inputEl.value.value = null
    emit('upload:done', uploaded)
  }

  if (uploadErr.value) {
    return
  }

  m.push(uploaded.value)
  model.value = m
}

async function remove() {
  const ex = existsFile.value
  if (ex) {
    await doRemove({ url: `upload/${file.value.url}` })
    const m = model.value
    const idx = m.findIndex(f => existsFile.value.division === f.division)
    m.splice(idx, 1)
    model.value = m
    emit('upload:remove', ex)
  }
  fileBlob.value = null
  meta.value = null
  if (inputEl.value) {
    inputEl.value.value = null
  }
}
</script>
